import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useImperativeHandle,
  memo,
} from "react";
import { Container, Graphics } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { useReelControl } from "../../../../Contexts/ReelControlContext";
import { SOUND_NAME } from "../../../../sounds";
import { soundManager } from "../../../../SoundManager";
import anime from "animejs/lib/anime.es.js";
import BuyBonusPopupController from "./BuyBonusPopupController";
import { safeInit } from "../../../../utilities";
import {
  selectBonusBuyActive,
  selectBonusBuyMultiplier,
  selectBalance,
  selectCurrency,
} from "../../../../State/selectors";
import { closeBonusBuy, closeMenu } from "../../../../State/actions";

// Overlay settings
const OVERLAY_BACKGROUND_COLOR = 0x090f41;
const OVERLAY_BACKGROUND_ALPHA = 0.6;
const OVERLAY_FADE_DURATION = 200;

const BuyBonusPopup = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { handleSpin } = useReelControl();
  const popupActive = useSelector(selectBonusBuyActive);

  const currentBet = useSelector((state) => state.game.bet);
  const bonusBuyMultiplier = useSelector(selectBonusBuyMultiplier);
  const balance = useSelector(selectBalance);
  const currency = useSelector(selectCurrency);

  const controllerRef = useRef(null);
  const containerRef = useRef(null);
  const overlayRef = useRef(null);
  const buyConfirmedRef = useRef(false);
  const dimensionsContext = useDimensions();
  const { dimensions } = dimensionsContext;

  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const overlayAnimRef = useRef(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);

  // Calculate bonus cost
  const bonusCost = currentBet * bonusBuyMultiplier;
  const canAffordBonus = balance >= bonusCost;

  // Expose fadeInBackground / fadeOutBackground via overlayRef
  useImperativeHandle(overlayRef, () => ({
    fadeInBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          if (callback && typeof callback === "function") callback();
        },
      });
    },
    fadeOutBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: OVERLAY_BACKGROUND_ALPHA },
        alpha: 0,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          setOverlayAlpha(0);
          if (callback && typeof callback === "function") callback();
        },
      });
    },
  }));

  // Initialize controller once
  useEffect(() => {
    if (containerRef.current && !controllerRef.current) {
      safeInit(() => {
        controllerRef.current = new BuyBonusPopupController(
          containerRef.current,
          dimensionsContext,
          dispatch,
          overlayRef,
          t,
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle dimension changes
  useEffect(() => {
    if (controllerRef.current) {
      controllerRef.current.updateDimensions(dimensionsContext);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensionsContext]);

  useEffect(() => {
    if (controllerRef.current && popupActive && !isClosing) {
      dispatch(closeMenu());

      setIsAnimatingIn(true);
      controllerRef.current.show(bonusCost, currency, () => {
        // Called when "in" animation finishes and idle begins
        setIsAnimatingIn(false);
      });

      // Make button interactive
      controllerRef.current.setButtonInteractive(() => {
        if (!canAffordBonus || isClosing || isAnimatingIn) return;
        buyConfirmedRef.current = true;
        soundManager.play(SOUND_NAME.button_click);
        setIsClosing(true);
      });

      // Apply disabled visual when player can't afford
      controllerRef.current.setButtonDisabledVisual(!canAffordBonus);

      // Close button (X) handler
      controllerRef.current.setCloseButtonHandler?.(() => {
        if (isClosing || isAnimatingIn) return;
        buyConfirmedRef.current = false;
        soundManager.play(SOUND_NAME.button_click);
        setIsClosing(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popupActive]);

  // Update cost when bet changes while popup is open
  useEffect(() => {
    if (popupActive && controllerRef.current?.isPlaying) {
      controllerRef.current.updateCost(bonusCost, currency);
      controllerRef.current.setButtonDisabledVisual(!canAffordBonus);
    }
  }, [bonusCost, popupActive, currency, canAffordBonus]);

  // Close animation
  useEffect(() => {
    if (!isClosing) return;

    if (controllerRef.current?.initialized) {
      controllerRef.current.hide(() => {
        dispatch(closeBonusBuy());
        setIsClosing(false);

        if (buyConfirmedRef.current) {
          buyConfirmedRef.current = false;
          handleSpin({ purchasedFeature: "bonus_buy" });
        }
      });
    } else {
      dispatch(closeBonusBuy());
      setIsClosing(false);

      if (buyConfirmedRef.current) {
        buyConfirmedRef.current = false;
        handleSpin({ purchasedFeature: "bonus_buy" });
      }
    }
  }, [isClosing, dispatch, handleSpin]);

  // Handle overlay click to close
  const handleBackgroundClick = useCallback(
    (evt) => {
      if (evt?.preventDefault) evt.preventDefault();
      if (evt?.stopPropagation) evt.stopPropagation();

      // Block clicks during intro animation — prevents stuck popup
      if (controllerRef.current && popupActive && !isClosing && !isAnimatingIn) {
        buyConfirmedRef.current = false;
        soundManager.play(SOUND_NAME.button_click);
        setIsClosing(true);
      }
    },
    [popupActive, isClosing, isAnimatingIn],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.cleanup();
      }
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
    };
  }, []);

  // Container position
  const containerPosition = useMemo(
    () => ({
      x: dimensions.width / 2,
      y: dimensions.height / 2,
    }),
    [dimensions.width, dimensions.height],
  );

  // Overlay draw
  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(OVERLAY_BACKGROUND_COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  return (
    <Container
      name="buy_bonus_popup"
      x={containerPosition.x}
      y={containerPosition.y}
      sortableChildren={true}
      zIndex={10000}
      ref={containerRef}
    >
      {/* Background overlay */}
      <Graphics
        name="buy_bonus_popup_overlay"
        draw={drawOverlay}
        alpha={overlayAlpha}
        x={-containerPosition.x}
        y={-containerPosition.y}
        zIndex={0}
        eventMode={popupActive && !isClosing && !isAnimatingIn ? "static" : "none"}
        pointerdown={handleBackgroundClick}
        cursor={popupActive && !isAnimatingIn ? "pointer" : "default"}
      />
    </Container>
  );
};

export default memo(BuyBonusPopup);
